import { useCallback, useEffect, useState } from 'react'
import { Modal, useDialogs } from '../ui'
import PdfPages from './PdfPages'
import { renderApiService } from '../../services/renderApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontSize, spacing } from '../../styles/theme'

/**
 * A render's PDF shown in the page. A password-protected one asks for its password in an app modal (never the
 * browser's prompt()); Cancel leaves the "password-protected" message. The password goes only to the in-browser
 * PDF reader and is never sent to the server.
 */
export default function PdfViewerModal({ renderJobId, onClose }: { renderJobId: string | null; onClose: () => void }) {
  const dialogs = useDialogs()
  const [file, setFile] = useState<Blob | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setFile(null); setError('')
    if (!renderJobId) return
    let live = true
    renderApiService.downloadOutput(renderJobId)
      .then(blob => { if (live) setFile(blob) })
      .catch(err => { if (live) setError(err instanceof ApiError ? err.message : 'The document could not be loaded.') })
    return () => { live = false }
  }, [renderJobId])

  const askPassword = useCallback((incorrect: boolean) => dialogs.prompt({
    title: incorrect ? 'Incorrect password' : 'Password required',
    message: incorrect ? 'That password did not open this PDF. Try again.' : 'This PDF is password-protected. Enter its password to view it.',
    label: 'Password', type: 'password', confirmLabel: 'Open', required: true,
  }), [dialogs.prompt])

  // The password prompt is a sibling (drawn on top), not a child; while it is open, Escape answers it, not the viewer.
  const promptOpen = !!dialogs.element
  return (
    <>
    <Modal open={!!renderJobId} onClose={() => { if (!promptOpen) onClose() }} title="View document" width={880}>
      {error
        ? <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>
        : !file
          ? <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, padding: spacing.lg }}>Loading document…</div>
          : <PdfPages file={file} askPassword={askPassword} />}
    </Modal>
    {dialogs.element}
    </>
  )
}
